<template>
  <div>
    <exo-table :root="_self" :formWidth="1300">
      <exo-form :root="_self">
        <template v-slot:titleTooltip>
          <exo-help-dialog
            :color="'black--text'"
            :margin="'m-0'"
            title="Orbit definition"
          >
            Five orbit definition types exist in ExoOPS: geostationary,
            circular, elliptical, equatorial and frozen. A geostationary orbit
            is defined by its parking longitude. A circular orbit enjoys a
            simplified description, in which the eccentricity parameter is not
            required. To define elliptical orbits six parameters remain
            necessary. For the equatorial orbits, the inclination and the RAAN
            are not needed. For more information, see the orbit type tooltip.
            <br />
            <br />
            Sun Synchronous orbits can be easily defined by ticking the
            corresponding case: the inclination of the orbit is then computed by
            the program and does not need to be specified.
            <br />
            Note: the specified inclination gives a perfectly SSO orbit only if
            the only perturbation taken into account is the
            <katex-element expression="J_2" />.
            <br />
            <br />
            The following figure shows the commonly used Keplerian elements,
            whose definition is recalled in the paragraph below.
            <br />
            <br />
            <v-img
              contain
              :src="require('../../assets/images/tooltip-orbit-definition.png')"
              aspect-ratio="1.8"
            />
            <br />
            <br />

            <b>Keplerian Elements</b>
            <br />
            <br />
            Semi-major axis (<katex-element expression="a" />)
            <br />
            Eccentricity (<katex-element expression="e" />)
            <br />
            Inclination (<katex-element expression="i" />)
            <br />
            Argument of perigee (<katex-element expression="\omega" />)
            <br />
            Right ascension of the ascending node (<katex-element
              expression="\Omega"
            />)
            <br />
            True anomaly (<katex-element expression="\nu" />)
            <br />
            <br />
            Note: elements <katex-element expression="a" /> and
            <katex-element expression="e" /> can be replaced by the altitudes of
            the apogee and perigee (the farthest and closest points to Earth
            respectively).
            <br />
            The following relationships between these parameters hold:
            <br />
            <div class="text-xs-center">
              <katex-element
                expression="h_a = r_a - R_{\oplus}= a(1+e) - R_{\oplus}"
              />
            </div>
            <div class="text-xs-center">
              <katex-element
                expression="h_p = r_p - R_{\oplus}= a(1-e) - R_{\oplus}"
              />
            </div>
            <br />
            <br />
            Where
            <br />
            <katex-element expression="h_a" />: altitude of apogee,
            <br />
            <katex-element expression="h_p" />: altitude of perigee,
            <br />
            <katex-element expression="r_a" />: orbit radius at apogee,
            <br />
            <katex-element expression="r_p" />: orbit radius at perigee,
            <br />
            <katex-element expression="R_{\oplus}" />: Earth's radius.
            <br />
            <br />
          </exo-help-dialog>
        </template>
        <v-row>
          <v-col cols="6">
            <exo-input-name :root="_self" />
            <exo-input-select :root="_self" name="type">
              <exo-help-dialog title="Orbit types">
                The available orbit types are the following:
                <br />
                <br />
                <ul>
                  <li>
                    <b>Elliptical</b>: Orbits with an eccentricity
                    <katex-element expression="e \in [0, 1[" />. If this type of
                    orbit is selected, all the Keplerian parameters can be set
                    by the user.
                  </li>
                  <br />
                  <li>
                    <b>Circular</b>: Orbits with zero eccentricity. The perigee
                    argument <katex-element expression="\omega" /> is not
                    uniquely defined for this kind of orbits and is arbitrarily
                    set to <katex-element expression="0" />.
                  </li>
                  <br />
                  <li>
                    <b>Equatorial</b>: Orbits with zero inclination. The right
                    ascension of the ascending node (RAAN) is not uniquely
                    defined and is arbitrarily set to
                    <katex-element expression="0" />.
                  </li>
                  <br />
                  <li>
                    <b>Geostationary (GEO)</b>: This is a circular equatorial
                    orbit whose period is one sidereal day (<katex-element
                      expression="86164.09890 \text{ s}"
                    />). If we consider Keplerian motion, the semi-major axis of
                    a geostationary orbit is equal to
                    <katex-element
                      expression="a_{GEO} = 42,164.159 \text{ km}"
                    />. This is the value used in the software. In this case,
                    the ground track reduces to a single point. To define this
                    orbit the user only needs to input the terrestrial longitude
                    of the sub-satellite point.
                  </li>
                  <br />
                  <li>
                    <b>Frozen</b>: Orbits that benefit from a null drift of the
                    (mean) eccentricity and the (mean) argument of perigee under
                    the influence of perturbations. These orbits are computed
                    considering only the J2 and J3 terms of the earth potential
                    (corresponding to a
                    <katex-element expression="(n, m) = (3, 0)" /> Earth
                    potential). This results in an argument of perigee of
                    <katex-element expression="90 \text{ deg}" /> and an
                    eccentricity of the order of
                    <katex-element expression="10^{-3}" />. Note that
                    considering a higher order potential or other orbital
                    perturbations will make the orbital parameters drift away
                    from their reference values.
                  </li>
                </ul>
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-select :root="_self" name="repeatOrbit">
              <exo-help-dialog title="Repeat ground track Orbits">
                <p>
                  Phased orbits can be defined using three parameters
                  <katex-element expression="N" />,
                  <katex-element expression="P" /> and
                  <katex-element expression="Q" /> such that
                  <br />
                  <katex-element
                    expression="T_c = (N \cdot Q + P) T_{mn} = Q \cdot T_{d}"
                  />
                  <br />
                  where <katex-element expression="T_c" /> is the duration of
                  the repeat cycle, <katex-element expression="T_{mn}" /> is the
                  mean nodal period and <katex-element expression="T_d" /> is
                  the orbital day, which represents the revolution period of the
                  planet with respect to the ascending node of the orbit.
                </p>
                <p>Additionally, the parameters relate so that:</p>
                <ul>
                  <li>
                    <katex-element expression="Q = T_c / T_d" /> is the number
                    of planet revolutions per repeat cycle.
                  </li>
                  <li>
                    <katex-element expression="N + P/Q = T_d / T_{mn}" /> is the
                    number of orbits per orbital day.
                  </li>
                  <li>
                    <katex-element expression="N \cdot Q + P = T_c/T_{mn}" /> is
                    the whole number of orbits per repeat cycle.
                  </li>
                </ul>
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-double
              v-if="isCircularType"
              :root="_self"
              name="altitude"
            />
            <exo-input-double
              v-if="isGEOType"
              :root="_self"
              name="parkingLongitude"
            />
            <exo-input-define-orbit
              :root="_self"
              v-if="this.isRequiredOrOptional('definitionType', inputs)"
              name="definitionType"
            />
            <exo-input-double
              :root="_self"
              v-if="this.isRequiredOrOptional('sma', inputs)"
              name="sma"
            />
            <exo-input-double
              :root="_self"
              v-if="this.isRequiredOrOptional('perigeeAltitude', inputs)"
              name="perigeeAltitude"
            />
            <exo-input-double
              :root="_self"
              v-if="this.isRequiredOrOptional('apogeeAltitude', inputs)"
              name="apogeeAltitude"
            />
            <exo-form-alert
              v-if="
                this.isRequiredOrOptional('apogeeAltitude', inputs) &&
                this.inputs.apogeeAltitude &&
                Number(this.inputs.apogeeAltitude) <
                  Number(this.inputs.perigeeAltitude)
              "
            >
              The perigee altitude must be lower than the apogee altitude.
            </exo-form-alert>
            <exo-input-double
              :root="_self"
              v-if="this.isRequiredOrOptional('eccentricity', inputs)"
              tooltip="Maneuver strategies are optimised for low eccentricity orbits. The eccentricity is limited to 0.1"
              name="eccentricity"
            />
            <exo-input-checkbox :root="_self" name="sunSynchronousOrbit">
              <exo-help-dialog
                margin="mt-0 ml-2"
                title="Sun Synchronous Orbits"
              >
                <br />
                <v-img
                  contain
                  :src="
                    require('../../assets/images/tooltip-sun-synchronous-orbit.png')
                  "
                  aspect-ratio="1.8"
                />
                <br />
                <p>
                  A Sun Synchronous orbit is one for which the dihedral angle
                  between the meridian plane containing the node and the one
                  containing the polar axis and the Sun is constant over time.
                </p>
                <p>
                  For such an orbit the node crossing always occurs at the same
                  local time.
                </p>
                <p>
                  Sun Synchronous orbits exist up to an altitude of about 6000
                  km. In ExoOPS the inclination of a Sun Synchronous orbit is
                  automatically computed.
                </p>
              </exo-help-dialog>
            </exo-input-checkbox>
            <v-row>
              <v-col cols="3">
                <exo-input-double :root="_self" name="n" />
              </v-col>
              <v-col cols="3" offset-md-1>
                <exo-input-double :root="_self" name="p" />
              </v-col>
              <v-col cols="3" offset-md-1>
                <exo-input-double :root="_self" name="q" />
              </v-col>
            </v-row>
            <exo-form-alert
              class="mb-2"
              type="error"
              v-if="isRequiredOrOptional('p', inputs) && inputs.p >= inputs.q"
            >
              Error: p must be smaller than q.
            </exo-form-alert>
            <exo-form-alert
              type="error"
              class="mb-2"
              v-if="errorTrace.includes('Invalid parameters for phased orbit.')"
            >
              Error: invalid parameters for phased orbit.
            </exo-form-alert>
            <exo-input-double
              v-if="!inputs.sunSynchronousOrbit"
              :root="_self"
              name="inclination"
            />
            <exo-input-double :root="_self" name="perigeeArgument" />
            <exo-input-select :root="_self" name="ascendingNodeDefinitionType">
              <exo-help-dialog title="Ascending node definition">
                The ascending node of the orbit can be specified by using the
                (mean) local time (MLTAN) instead than the right ascension value
                (RAAN).
                <br />
                If the MLTAN is used, the corresponding value of the RAAN will
                be evaluated from it. By default the definition date of the
                orbit is used but it can be overloaded in the mission
                configuration.
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-double :root="_self" name="raan" />
            <exo-input-double :root="_self" name="ltan" />
            <exo-input-double :root="_self" name="anomaly" />
            <exo-input-select :root="_self" name="anomalyType" />
            <exo-input-select :root="_self" name="meanOscType">
              <exo-help-dialog title="Mean and Osculating elements">
                Perturbations dramatically change the spacecraft's trajectory
                with respect to the idealized Keplerian description. Perturbed
                motion can be seen as the sum of a slowly varying part
                (resulting from <em>secular</em> and
                <em>long-periodic</em> perturbations) and of a fast contribution
                (the <em>short-periodic</em> terms for which the period equals
                the orbital period).
                <br />
                <br />
                <b>Osculating elements</b>: these are the instantaneous orbital
                elements taking into account the effect of perturbations acting
                on the spacecraft.
                <br />
                <b>Mean elements</b>: these elements account for secular and
                long periodic variations only. This means that the short
                periodic effects (less than the orbital period) of perturbations
                are filtered out. Thus, mean elements change slowly with time
                (except for the anomaly). Usually they can be derived from
                analytical theories or by numerical procedures.
                <br />
                <br />
                Note: <em>mean elements</em> needs to be understood in the sense
                of a given model. In ExoOPS the DSST model is used for
                converting from mean to osculating elements and vice-versa.
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-date-time :root="_self" name="orbitDateTime">
              <exo-help-dialog
                margin="mt-2 ml-2"
                title="Orbit RAAN and MLTAN reference date"
              >
                This is the date at which the orbital elements are exactly those
                specified in input. In particular, for a NON SSO orbit, this is
                the date when the orbit has the specified RAAN/MLTAN.
                <br />
                <br />
                Note: We recall that if the perturbation caused by Earth's
                oblateness (<katex-element expression="J_2" />) is taken into
                account, the RAAN changes linearly with time. For this reason,
                the mean local time at the ascending node (MLTAN) is not
                constant unless the orbit is Sun Synchronous (SSO). In fact in
                the latter case (SSO) the node's drift rate will match exactly
                the Earth's rotation rate in its motion around the Sun.
              </exo-help-dialog>
            </exo-input-date-time>
          </v-col>
          <v-col cols="6">
            <exo-card-form class="ml-2" label="Orbit visualization">
              <cesium-viewer
                :tracesResults="this.tracesResults"
                :loading="isCesiumLoading"
                :orbitDate="this.inputs.orbitDateTime"
                :timestep="this.timestep"
              />
              <exo-form-alert type="warning" v-if="errorTrace !== ''">
                {{ errorTrace }}
              </exo-form-alert>
            </exo-card-form>
            <exo-card-form
              class="ml-2"
              label="Phased Orbit finder"
              v-if="
                this.inputs.repeatOrbit === 'True' &&
                this.inputs.type !== 'Frozen'
              "
            >
              <exo-npq-finder
                :root="this"
                :npqBuilderWatcher="npqBuilderWatcher"
                :useSunSynchronousInclination="this.inputs.sunSynchronousOrbit"
                :isEllipticalType="isEllipticalType"
              />
            </exo-card-form>
          </v-col>
        </v-row>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import numericalMixin from '../../mixins/numericalForms.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import dataTools from '../../tools/data.js'
import NumericalOrbitViewer from './NumericalOrbitViewer.vue'
import moment from 'moment'

export default {
  name: 'orbitdefinition',

  mixins: [formMixin, numericalMixin],
  components: {
    'cesium-viewer': NumericalOrbitViewer
  },

  mounted() {
    this.computeEphemerisTraces()
  },

  computed: {
    npqBuilderWatcher() {
      return [
        this.inputs.repeatOrbit,
        this.inputs.minAltitude,
        this.inputs.maxAltitude,
        this.inputs.minQ,
        this.inputs.maxQ,
        this.inputs.minSMA,
        this.inputs.maxSMA,
        this.inputs.sunSynchronousOrbit,
        this.inputs.eccentricity,
        this.inputs.inclination
      ]
    },
    displayNPQPlot() {
      return (
        this.inputs.repeatOrbit === 'True' &&
        ((this.inputs.minAltitude && this.inputs.maxAltitude) ||
          (this.inputs.minSMA && this.inputs.maxSMA)) &&
        this.inputs.minQ &&
        this.inputs.maxQ
      )
    },
    isCircularType() {
      return this.inputs.type === 'Circular'
    },
    isEllipticalType() {
      return this.inputs.type === 'Elliptical'
    },
    isGEOType() {
      return this.inputs.type === 'Geostationary'
    },
    isEquatorialType() {
      return this.inputs.type === 'Equatorial'
    },
    isFrozenType() {
      return this.inputs.type === 'Frozen'
    },
    isDefinedBy() {
      return this.inputs.definitionType
    },
    traceBuilderWatcher() {
      return [
        this.inputs.altitude,
        this.inputs.perigeeAltitude,
        this.inputs.eccentricity,
        this.inputs.apogeeAltitude,
        this.inputs.sma,
        this.inputs.inclination,
        this.inputs.definitionType,
        this.inputs.sunSynchronousOrbit,
        this.inputs.repeatOrbit,
        this.inputs.n,
        this.inputs.p,
        this.inputs.q,
        this.inputs.perigeeArgument,
        this.inputs.raan,
        this.inputs.ltan,
        this.inputs.ascendingNodeDefinitionType,
        this.inputs.anomalyType,
        this.inputs.anomaly,
        this.inputs.orbitDateTime,
        this.inputs.visualizationDate,
        this.inputs.parkingLongitude
      ]
    }
  },
  watch: {
    traceBuilderWatcher() {
      setTimeout(() => {
        this.computeEphemerisTraces()
      }, 200)
    }
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        { value: 'type', text: 'Type', sortable: true },
        {
          value: 'sunSynchronousOrbit',
          text: 'Sun-synchronous orbit',
          sortable: true
        },
        {
          value: 'altitude',
          text: 'Altitude' + this.getDefaultUnitLabel('altitude'),
          sortable: true
        },
        {
          value: 'perigeeAltitude',
          text:
            'Perigee altitude' + this.getDefaultUnitLabel('perigeeAltitude'),
          sortable: true
        },
        { value: 'eccentricity', text: 'Eccentricity', sortable: true },
        {
          value: 'inclination',
          text: 'Inclination' + this.getDefaultUnitLabel('inclination'),
          sortable: true
        },
        {
          value: 'raan',
          text: 'RAAN' + this.getDefaultUnitLabel('raan'),
          sortable: true
        },
        {
          value: 'ltan',
          text: 'MLTAN' + this.getDefaultUnitLabel('ltan'),
          sortable: true
        },
        /*
        {value: 'parkingLongitude', text: 'Parking longitude' + this.getDefaultUnitLabel('parkingLongitude'), sortable: true},
        {value: 'n', text: 'N', sortable: true},
        {value: 'p', text: 'P', sortable: true},
        {value: 'q', text: 'Q', sortable: true}, */
        { value: 'repeatOrbit', text: 'Repeat Orbit', sortable: true },
        { value: 'actions', text: 'Actions', sortable: true }
      ],

      enumPropertiesList: this.getEnumProperties(),

      modalRef: { content: {} },
      showModal: false,

      tracesResults: {},
      timestep: '',
      errorTrace: '',

      inputs: {
        type: this.getDefaultValue('type'),
        altitude: this.getDefaultValue('altitude'),
        perigeeAltitude: this.getDefaultValue('perigeeAltitude'),
        eccentricity: this.getDefaultValue('eccentricity'),
        apogeeAltitude: this.getDefaultValue('apogeeAltitude'),
        sma: this.getDefaultValue('sma'),
        inclination: this.getDefaultValue('inclination'),
        definitionType: this.getDefaultValue('definitionType'),
        sunSynchronousOrbit: this.getDefaultValue('sunSynchronousOrbit'),
        repeatOrbit: this.getDefaultValue('repeatOrbit'),
        n: this.getDefaultValue('n'),
        p: this.getDefaultValue('p'),
        q: this.getDefaultValue('q'),
        perigeeArgument: this.getDefaultValue('perigeeArgument'),
        raan: this.getDefaultValue('raan'),
        ltan: this.getDefaultValue('ltan'),
        ascendingNodeDefinitionType: this.getDefaultValue(
          'ascendingNodeDefinitionType'
        ),
        anomalyType: this.getDefaultValue('anomalyType'),
        anomaly: this.getDefaultValue('anomaly'),
        meanOscType: this.getDefaultValue('meanOscType'),
        parkingLongitude: this.getDefaultValue('parkingLongitude'),
        orbitDateTime: this.getDefaultValue('orbitDateTime'),
        visualizationDate: this.getDefaultValue('visualizationDate'),
        maxQ: this.getDefaultValue('qMax'),
        minQ: this.getDefaultValue('qMin'),
        minAltitude: this.getDefaultValue('altitudeMin'),
        maxAltitude: this.getDefaultValue('altitudeMax'),
        scope: this.getDefaultValue('scope')
      },
      isCesiumLoading: false
    }
  },

  methods: {
    async computeEphemerisTraces() {
      this.isCesiumLoading = true
      if (this.inputs.inclination >= 0 && this.inputs.inclination < 180) {
        let data = await this.$http
          .post('/physics/numericalorbittraces', this.createObjectFromForm())
          .then(function (response) {
            let date0 = moment.utc(response.data['orbit'].dates[0])
            let date1 = moment.utc(response.data['orbit'].dates[1])

            return {
              groundTraces: response.data['ground'].traces,
              orbitTraces: response.data['orbit'].traces,
              timestep: date1.diff(date0, 'seconds')
            }
          })
          .catch((e, response) => {
            this.errorTrace = this.getErrorMessage(e)
            return null
          })

        if (data == null) {
          this.tracesResults = null
        } else {
          this.errorTrace = ''
          let result = {
            ground: [data.groundTraces],
            orbit: [data.orbitTraces]
          }
          this.tracesResults = result
          this.timestep = data.timestep
        }
      }
      this.isCesiumLoading = false
    },

    getRequiredPropertiesList(instance) {
      let orbitType = this.getOrbitType(instance)
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        orbitType,
        'required'
      )
      if (
        requiredList.includes('sunSynchronousOrbit') &&
        !instance.sunSynchronousOrbit
      ) {
        requiredList.push('inclination')
      }
      return requiredList
    },

    getOptionalPropertiesList(instance) {
      let orbitType = this.getOrbitType(instance)
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        orbitType,
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      optionalList.push('inclination') // to display in the table
      return [...new Set(requiredList.concat(optionalList))]
    },

    getOrbitType(instance) {
      let types = { type: instance.type }
      if (instance.type === 'Elliptical' || instance.type === 'Equatorial') {
        types.definitionType = instance.definitionType
      }
      types.ascendingNodeDefinitionType = instance.ascendingNodeDefinitionType
      types.repeatOrbit = instance.repeatOrbit
      return types
    },

    getCurrentFormType() {
      return 'OrbitDefinition'
    },

    getRefList(param) {
      return []
    }
  }
}
</script>
